import useSWR, { mutate } from "swr";

import axios, { endpoints, fetcher } from "src/utils/axios";
import { PlayerDataProblem, PlayerFieldKey, PlayerReadiness, PlayerRequirements } from "src/types/player-requirements";

type CompletionHandler = (problem: PlayerDataProblem) => Promise<boolean>;
let completionHandler: CompletionHandler | undefined;

export function registerPlayerCompletionHandler(handler: CompletionHandler) {
  completionHandler = handler;
  return () => { if (completionHandler === handler) completionHandler = undefined; };
}

export function usePlayerRequirements(providerId?: number) {
  return useSWR<PlayerRequirements>(providerId ? `/providers/${providerId}/player-requirements` : null, fetcher);
}

export async function readPlayerReadiness(providerId: number, action: string): Promise<PlayerReadiness> {
  const response = await axios.get(`/providers/${providerId}/player-requirements/me`, { params: { action } });
  return response.data;
}

export async function completePlayerProfile(values: Partial<Record<PlayerFieldKey, string>>) {
  const response = await axios.patch("/providers/player-requirements/me", values);
  await mutate(endpoints.auth.profile);
  return response.data;
}

export function playerDataProblem(error: unknown): PlayerDataProblem | undefined {
  const normalized = error as { detail?: PlayerDataProblem; response?: { data?: { detail?: PlayerDataProblem } } };
  const detail = normalized?.response?.data?.detail ?? normalized?.detail;
  return detail?.code === "player_data_required" ? detail : undefined;
}

export async function ensurePlayerReadiness(providerId: number, action: string) {
  if (!["reservation", "training", "reservationperiod", "waitlist", "pass", "ticket", "tournamentapplication"].includes(action)) return;
  const readiness = await readPlayerReadiness(providerId, action);
  if (readiness.ready) return;
  const accepted = completionHandler && await completionHandler({ ...readiness, code: "player_data_required" });
  if (!accepted) throw new Error("player_data_cancelled");
  const checked = await readPlayerReadiness(providerId, action);
  if (!checked.ready) throw new Error("player_data_still_required");
}

export async function withPlayerRequirements<T>(operation: () => Promise<T>, retry = true): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    const problem = playerDataProblem(error);
    if (!problem || !completionHandler) throw error;
    const accepted = await completionHandler(problem);
    if (!accepted) throw new Error("player_data_cancelled");
    if (!retry) throw new Error("player_data_saved_retry");
    // Only retry once, after an explicit completion action. Server checks remain authoritative.
    return operation();
  }
}
